<script setup>
import { ref, reactive, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ResponseForm from './ResponseForm.vue';
import DocumentUploadForm from './DocumentUploadForm.vue';
import UrlSyncForm from './UrlSyncForm.vue';

const props = defineProps({
  selectedResponse: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const route = useRoute();

const dialogRef = ref(null);
const responseForm = ref(null);

const state = reactive({ creationMode: 'manual' });

const creationModeOptions = computed(() => [
  { value: 'manual', label: t('CAPTAIN.RESPONSES.CREATION_MODE.MANUAL') },
  { value: 'document', label: t('CAPTAIN.RESPONSES.CREATION_MODE.DOCUMENT') },
  { value: 'url', label: t('CAPTAIN.RESPONSES.CREATION_MODE.URL') },
]);

// ResponseForm.vue nests fields under assistant_response[...] (both the
// JSON and FormData payloads), matching what the controller's
// require(:assistant_response) expects — so extras that belong to the
// same strong-params allowlist (e.g. assistant_id) must land inside that
// nesting too. FormData also does not expose its entries as enumerable
// own properties, so it can't go through the spread-based
// { id, ...responseDetails } payload that storeFactory's updateRecord
// destructures — append extra fields directly onto it instead, and
// dispatch it with the id kept out of band.
const appendPayloadExtras = (responseDetails, extras) => {
  if (responseDetails instanceof FormData) {
    Object.entries(extras).forEach(([key, value]) =>
      responseDetails.append(`assistant_response[${key}]`, value)
    );
    return responseDetails;
  }
  return {
    assistant_response: { ...responseDetails.assistant_response, ...extras },
  };
};

const updateResponse = responseDetails =>
  responseDetails instanceof FormData
    ? store.dispatch('captainResponses/updateWithFormData', {
        id: props.selectedResponse.id,
        formData: responseDetails,
      })
    : store.dispatch('captainResponses/update', {
        id: props.selectedResponse.id,
        ...responseDetails,
      });

const i18nKey = computed(() => `CAPTAIN.RESPONSES.${props.type.toUpperCase()}`);

const createResponse = responseDetails =>
  store.dispatch('captainResponses/create', responseDetails);

const createTrainingDocument = formData => {
  formData.append('training_document[assistant_id]', route.params.assistantId);
  return store.dispatch('captainTrainingDocuments/create', formData);
};

const createTrainingUrl = ({ external_link: externalLink }) =>
  store.dispatch('captainTrainingUrls/create', {
    training_url: {
      assistant_id: route.params.assistantId,
      external_link: externalLink,
    },
  });

const handleSubmit = async updatedResponse => {
  try {
    const responseWithAssistant = appendPayloadExtras(updatedResponse, {
      assistant_id: route.params.assistantId,
    });

    if (props.type === 'edit') {
      await updateResponse(responseWithAssistant);
    } else {
      await createResponse(responseWithAssistant);
    }
    useAlert(t(`${i18nKey.value}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      error?.response?.message || t(`${i18nKey.value}.ERROR_MESSAGE`);
    useAlert(errorMessage);
  }
};

const handleDocumentSubmit = async formData => {
  try {
    await createTrainingDocument(formData);
    useAlert(t('CAPTAIN.TRAINING_DOCUMENTS.SUCCESS_MESSAGE'));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('CAPTAIN.TRAINING_DOCUMENTS.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleUrlSubmit = async urlDetails => {
  try {
    await createTrainingUrl(urlDetails);
    useAlert(t('CAPTAIN.TRAINING_URLS.SUCCESS_MESSAGE'));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('CAPTAIN.TRAINING_URLS.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleClose = () => {
  state.creationMode = 'manual';
  emit('close');
};

const handleCancel = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t(`${i18nKey}.TITLE`)"
    :description="$t('CAPTAIN.RESPONSES.FORM_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    @close="handleClose"
  >
    <div v-if="type === 'create'" class="flex flex-col gap-1 mb-2">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.RESPONSES.CREATION_MODE.LABEL') }}
      </label>
      <ComboBox
        v-model="state.creationMode"
        :options="creationModeOptions"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <ResponseForm
      v-if="state.creationMode === 'manual'"
      ref="responseForm"
      :mode="type"
      :response="selectedResponse"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
    <DocumentUploadForm
      v-else-if="state.creationMode === 'document'"
      @submit="handleDocumentSubmit"
      @cancel="handleCancel"
    />
    <UrlSyncForm v-else @submit="handleUrlSubmit" @cancel="handleCancel" />
    <template #footer />
  </Dialog>
</template>
